/**
 * The tools Claude calls during a chat. They run here, on this device, against
 * the local database: notes never leave the device except as tool results, and
 * pages hidden from the AI (see privacy.ts) are never part of one.
 *
 * The tool definitions (names, descriptions, schemas) live in the `ai` Edge
 * Function; these are their implementations.
 */
import { addDays, isOpen, toIsoDate, type Id } from '@nexus/core';
import { serializeMarkdown } from '@nexus/markdown';
import type { Store } from '@nexus/store';
import { loadPageJSON, loadTasks, type Task } from '@nexus/workspace';
import { aiScope, type AiScope } from './privacy';

export const TOOL_NAMES = ['search_notes', 'read_page', 'list_tasks'] as const;
export type ToolName = (typeof TOOL_NAMES)[number];

/** Characters of page text per read_page call; longer pages are read in parts. */
export const PAGE_CHUNK = 30_000;
const MAX_SEARCH = 15;
const MAX_TASKS = 60;
const MAX_CHILDREN = 60;

export interface ToolOutput {
  content: string;
  isError?: boolean;
  /** Pages whose content was read (shown as sources under the answer). */
  read?: Id[];
}

/** Everything a tool needs; the scope is computed once per chat turn. */
export interface ToolContext {
  store: Store;
  scope: AiScope;
  today: string;
}

export async function toolContext(store: Store, now = new Date()): Promise<ToolContext> {
  return { store, scope: aiScope(await store.listPagesWithProps()), today: toIsoDate(now) };
}

const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
const int = (v: unknown, fallback: number, min: number, max: number) =>
  typeof v === 'number' && Number.isFinite(v)
    ? Math.min(max, Math.max(min, Math.floor(v)))
    : fallback;
const clean = (s: string) => s.replace(/\s+/g, ' ').trim();

export async function runTool(ctx: ToolContext, name: string, input: unknown): Promise<ToolOutput> {
  const args = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  switch (name) {
    case 'search_notes':
      return searchNotes(ctx, str(args.query), int(args.limit, 8, 1, MAX_SEARCH));
    case 'read_page':
      return readPage(ctx, str(args.page_id), int(args.offset, 0, 0, Number.MAX_SAFE_INTEGER));
    case 'list_tasks':
      return listTasks(ctx, str(args.scope) || 'today', str(args.under) || null);
    default:
      return { content: `Unknown tool: ${name}`, isError: true };
  }
}

async function searchNotes(ctx: ToolContext, query: string, limit: number): Promise<ToolOutput> {
  if (!query) return { content: 'Give a search query.', isError: true };
  const { store, scope } = ctx;
  let hits = (await store.search(query, limit * 4)).filter((h) => scope.visible.has(h.pageId));
  // Every word must match; with no hits, rank pages by how many of the words they contain.
  const words = query.match(/[\p{L}\p{N}_]+/gu) ?? [];
  if (hits.length === 0 && words.length > 1) {
    const score = new Map<Id, { hit: (typeof hits)[number]; n: number }>();
    for (const w of words) {
      for (const h of await store.search(w, limit * 4)) {
        if (!scope.visible.has(h.pageId)) continue;
        const s = score.get(h.pageId);
        if (s) s.n++;
        else score.set(h.pageId, { hit: h, n: 1 });
      }
    }
    hits = [...score.values()].sort((a, b) => b.n - a.n).map((s) => s.hit);
  }
  hits = hits.slice(0, limit);
  if (hits.length === 0) return { content: `No notes match "${query}".` };
  const lines = hits.map((h) => {
    const p = scope.visible.get(h.pageId);
    const path = p?.parentId ? ` (in [[${scope.visible.get(p.parentId)?.title ?? ''}]])` : '';
    // The snippet on its own line, so nothing sticks to the page_id.
    return `- [[${h.title || 'Untitled'}]] page_id=${h.pageId}${path}\n  ${clean(h.snippet)}`;
  });
  return { content: `${hits.length} result(s) for "${query}":\n${lines.join('\n')}` };
}

/** Internal properties (database schemas, templates) are not useful to the model. */
const visibleProps = (props: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(props).filter(([k]) => !k.startsWith('nexus.')));

async function readPage(ctx: ToolContext, pageId: string, offset: number): Promise<ToolOutput> {
  const { store, scope } = ctx;
  if (scope.excluded.has(pageId))
    return { content: 'This page is private: it is never shared with the AI.', isError: true };
  const page = scope.visible.get(pageId);
  if (!page) return { content: `No page with page_id=${pageId}.`, isError: true };

  const props = visibleProps(page.props);
  const md = serializeMarkdown(await loadPageJSON(store, pageId), {
    frontmatter: Object.keys(props).length ? props : null,
    titleOf: (id) => scope.visible.get(id)?.title,
  });
  const parent = page.parentId ? scope.visible.get(page.parentId) : undefined;
  const children = [...scope.visible.values()].filter((p) => p.parentId === pageId);
  const head = [
    `# ${page.title || 'Untitled'}`,
    `page_id=${pageId}`,
    parent ? `Parent: [[${parent.title}]] page_id=${parent.id}` : null,
    `Last edited: ${new Date(page.updatedAt).toISOString().slice(0, 16).replace('T', ' ')}`,
  ].filter(Boolean);

  const part = md.slice(offset, offset + PAGE_CHUNK);
  const rest = md.length - offset - part.length;
  const out = [head.join('\n'), '', part || '(empty page)'];
  if (rest > 0)
    out.push(
      '',
      `[${rest} more characters: call read_page with offset=${offset + part.length} to continue]`,
    );
  if (offset === 0 && children.length) {
    out.push('', `Subpages (${children.length}):`);
    for (const c of children.slice(0, MAX_CHILDREN)) out.push(`- [[${c.title}]] page_id=${c.id}`);
    if (children.length > MAX_CHILDREN) out.push(`- … ${children.length - MAX_CHILDREN} more`);
  }
  return { content: out.join('\n'), read: [pageId] };
}

export const TASK_SCOPES = ['today', 'week', 'overdue', 'inbox', 'open'] as const;

/** Same rules as the task views and the dashboard. */
export function tasksInScope(tasks: Task[], scope: string, today: string): Task[] | null {
  const open = tasks.filter((x) => isOpen(x.status));
  const week = addDays(today, 6);
  switch (scope) {
    case 'today':
      return open.filter((x) => (x.due && x.due <= today) || (x.scheduled && x.scheduled <= today));
    case 'week':
      return open.filter((x) => x.due && x.due <= week);
    case 'overdue':
      return open.filter((x) => x.due && x.due < today);
    case 'inbox':
      return open.filter((x) => !x.due && !x.scheduled && !x.projectId);
    case 'open':
      return open;
    default:
      return null;
  }
}

/** A page and everything under it. */
function within(ctx: ToolContext, pageId: Id, rootId: Id): boolean {
  for (let id: Id | null = pageId, i = 0; id && i < 100; i++) {
    if (id === rootId) return true;
    id = ctx.scope.visible.get(id)?.parentId ?? null;
  }
  return false;
}

async function listTasks(ctx: ToolContext, scope: string, under: Id | null): Promise<ToolOutput> {
  const { store, scope: ai, today } = ctx;
  if (under && !ai.visible.has(under))
    return { content: `No page with page_id=${under}.`, isError: true };
  const all = (await loadTasks(store)).filter(
    (t) =>
      ai.visible.has(t.pageId) && (!under || t.projectId === under || within(ctx, t.pageId, under)),
  );
  const tasks = tasksInScope(all, scope, today);
  if (!tasks) return { content: `Unknown scope "${scope}".`, isError: true };
  if (tasks.length === 0) return { content: `No open tasks for "${scope}" (today is ${today}).` };
  const sorted = [...tasks].sort((a, b) =>
    `${a.due ?? a.scheduled ?? '9999'} ${a.dueTime ?? ''}`.localeCompare(
      `${b.due ?? b.scheduled ?? '9999'} ${b.dueTime ?? ''}`,
    ),
  );
  const lines = sorted.slice(0, MAX_TASKS).map((t) => {
    const facts = [
      t.due ? `due ${t.due}${t.dueTime ? ` ${t.dueTime}` : ''}` : null,
      t.scheduled ? `scheduled ${t.scheduled}` : null,
      t.priority ? `priority ${t.priority}` : null,
      t.status !== 'todo' ? `status ${t.status}` : null,
      t.projectId && ai.visible.has(t.projectId)
        ? `project [[${ai.visible.get(t.projectId)!.title}]]`
        : null,
      t.tags.length ? t.tags.map((x) => `#${x}`).join(' ') : null,
    ].filter(Boolean);
    const where = t.kind === 'page' ? 'task page' : `in [[${t.sourceTitle}]]`;
    return `- ${clean(t.title)} (${[...facts, `${where} page_id=${t.pageId}`, `task_id=${t.id}`].join(', ')})`;
  });
  if (sorted.length > MAX_TASKS) lines.push(`- … ${sorted.length - MAX_TASKS} more`);
  return {
    content: `${tasks.length} task(s), scope "${scope}", today is ${today}:\n${lines.join('\n')}`,
  };
}
